/* eslint-disable max-lines */
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useReducer,
  useRef,
  useState
} from 'react';
import {
  startsTextUndoStep,
  TextUndoStep
} from '@utils/editors/text-undo-step.ts';
import { Session } from '@utils/types/editors/sessions.ts';
import {
  CaptionScriptixDescendant,
  DocumentSubtitleSegmentV1,
  DocumentSubtitleV1
} from '@utils/types/editors/subtitles/subtitle.ts';
import {
  DocumentObjectInnerDocument,
  DocumentResult
} from '@utils/types/editors/generic.ts';
import { useUpdateDocument } from '@utils/api/documents/use-update-document.tsx';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { QueryKeys } from '@utils/api/query-keys.ts';
import ConcurrentEditBanner from '@components/editors/concurrent-edit-banner.tsx';
import { useAudio } from '@utils/providers/audio-context.tsx';
import { ReactEditor } from 'slate-react';
import { Descendant } from 'slate';
import {
  clearCaptionFromLocalStorage,
  loadCaptionFromLocalStorage,
  markAsSynced,
  saveCaptionToLocalStorage
} from '@utils/localStorage/caption-storage.ts';
import isEqual from 'fast-deep-equal';
import { resolveDraftRecovery } from '@utils/localStorage/recovery-decision.ts';
import {
  hasPendingLocalChanges,
  resolveAutosave
} from '@utils/editors/autosave-decision.ts';
import {
  getBaseline,
  lastModifiedFromUpdateResponse,
  notifyRemoteEdit,
  probeServerLastModified,
  setBaseline,
  syncKey
} from '@utils/editors/document-sync-baseline.ts';
import DraftConflictModal from '@components/atoms/modals/draft-conflict-modal.tsx';
import {
  DraftConflictData,
  forceCloudSourceRequested,
  openCloudVersionInNewTab
} from '@utils/localStorage/draft-conflict.ts';
import toast from 'react-hot-toast';
import { CaptionContext, CaptionContextType } from './caption-context.tsx';
import {
  getDocument,
  useGetDocument
} from '@utils/api/documents/use-get-document.tsx';

// ---- Reducer ----

type State = {
  session?: Session;
  document?: DocumentSubtitleV1;
  documentResult?: DocumentResult;
  query: string;
  clickedIndex: number | null;
  undoStack: DocumentSubtitleV1[];
  redoStack: DocumentSubtitleV1[];
  search: string;
  currentMatch: DocumentSubtitleSegmentV1[] | null;
  caseSensitive: boolean;
  wholeWord: boolean;
  isDirty?: boolean;
  saveStatus: 'unsaved' | 'saved-locally' | 'synced' | 'saving';
  lastLocalSave: number | null;
  lastServerSync: number | null;
  documentVersion: number;
};

type Action = {
  type: string;
  payload: unknown;
};

const initialState: State = {
  session: undefined,
  document: undefined,
  query: '',
  clickedIndex: null,
  undoStack: [],
  redoStack: [],
  search: '',
  currentMatch: null,
  caseSensitive: false,
  wholeWord: false,
  isDirty: false,
  saveStatus: 'synced',
  lastLocalSave: null,
  lastServerSync: null,
  documentVersion: 0
};

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'SET_QUERY':
      return { ...state, query: String(action.payload) };
    case 'SET_SESSION':
      return { ...state, session: action.payload as Session };
    case 'SET_DOCUMENT':
      return { ...state, document: action.payload as DocumentSubtitleV1 };
    case 'SET_CLICKED_INDEX':
      return { ...state, clickedIndex: Number(action.payload) };
    case 'SET_UNDO_STACK':
      return { ...state, undoStack: action.payload as DocumentSubtitleV1[] };
    case 'SET_REDO_STACK':
      return { ...state, redoStack: action.payload as DocumentSubtitleV1[] };
    case 'SET_DOCUMENT_RESULT':
      return { ...state, documentResult: action.payload as DocumentResult };
    case 'SET_SEARCH':
      return { ...state, search: action.payload as string };
    case 'SET_CURRENT_MATCH':
      return {
        ...state,
        currentMatch: !action.payload
          ? null
          : (action.payload as DocumentSubtitleSegmentV1[])
      };
    case 'SET_CASE_SENSITIVE':
      return { ...state, caseSensitive: Boolean(action.payload) };
    case 'SET_WHOLE_WORD':
      return { ...state, wholeWord: Boolean(action.payload) };
    case 'SET_IS_DIRTY':
      return { ...state, isDirty: Boolean(action.payload) };
    case 'SET_SAVE_STATUS':
      return {
        ...state,
        saveStatus: action.payload as
          | 'unsaved'
          | 'saved-locally'
          | 'synced'
          | 'saving'
      };
    case 'SET_LAST_LOCAL_SAVE':
      return { ...state, lastLocalSave: action.payload as number | null };
    case 'SET_LAST_SERVER_SYNC':
      return { ...state, lastServerSync: action.payload as number | null };
    case 'INCREMENT_DOCUMENT_VERSION':
      return { ...state, documentVersion: state.documentVersion + 1 };
    default:
      return state;
  }
}

// ---- Public handle exposed via ref ----

export interface TranslationCaptionHandle {
  saveDocument: (userAction?: boolean) => void;
  isDirty: boolean;
  saveStatus: 'unsaved' | 'saved-locally' | 'synced' | 'saving';
  autoSave: () => void;
  /** Document-level undo/redo, so the header buttons reach the translation. */
  undo: () => void;
  redo: () => void;
}

// ---- Provider props ----

interface TranslationCaptionProviderProps {
  children: React.ReactNode;
  session: Session;
  sessionId: string;
  documentId: string | null;
  /** Ref to expose save / isDirty to parent */
  handleRef?: React.Ref<TranslationCaptionHandle>;
  /**
   * Fires when the translation pane's dirty flag flips. The ref-based
   * `isDirty` on `handleRef` is a snapshot captured in
   * `useImperativeHandle` and doesn't re-trigger renders in the parent,
   * so without this callback the beforeunload prompt stays attached
   * after a successful save until some other state change forces a
   * re-render.
   */
  onDirtyChange?: (isDirty: boolean) => void;
}

// ---- Provider ----

// Size-aware undo depth — each snapshot is a full-document copy, so scale the
// cap down as the document grows to protect the mobile per-tab memory ceiling
// (mirrors caption-context.tsx; see docs/MEMORY_OPTIMIZATION.md).
function getMaxHistory(doc?: DocumentSubtitleV1): number {
  const n = doc?.document?.length ?? 0;
  if (n > 20000) return 3;
  if (n > 8000) return 5;
  if (n > 3000) return 15;
  return 50;
}
const LOCALSTORAGE_DEBOUNCE_MS = 1000;

export function TranslationCaptionProvider({
  children,
  session,
  sessionId,
  documentId,
  handleRef,
  onDirtyChange
}: TranslationCaptionProviderProps) {
  const { t } = useTranslation();
  const { setTime, currentTime } = useAudio();
  const queryClient = useQueryClient();
  const [hasRemoteEdit, setHasRemoteEdit] = useState(false);

  // Fetch the translation document
  const { data: fetchedDocument } = useGetDocument({
    sessionId,
    documentId: documentId ?? undefined
  });

  // Track recovery
  const hasRecoveredData = useRef(false);
  const initialStateRef = useRef<State | null>(null);
  // Holds the unresolved local-vs-cloud conflict, if any (see DraftConflictModal).
  const initialConflictRef =
    useRef<DraftConflictData<DocumentSubtitleV1> | null>(null);

  // Build initial state (runs once per mount)
  if (initialStateRef.current === null) {
    const apiDocument = fetchedDocument?.result?.document as DocumentSubtitleV1;

    let recoveredData: {
      document: DocumentSubtitleV1;
      undoStack: DocumentSubtitleV1[];
      redoStack: DocumentSubtitleV1[];
      isDirty: boolean;
      recoveryTimestamp: number;
    } | null = null;

    // The "view cloud version" tab bypasses local recovery entirely.
    const forceCloud = forceCloudSourceRequested();

    if (sessionId && documentId && !forceCloud) {
      const localData = loadCaptionFromLocalStorage(sessionId, documentId);
      if (localData && localData.document) {
        let serverTimestamp = 0;
        if (fetchedDocument?.result?.last_modified) {
          const parsedTime = new Date(
            fetchedDocument.result.last_modified
          ).getTime();
          serverTimestamp = isNaN(parsedTime) ? 0 : parsedTime;
        }
        const localTimestamp = localData.lastLocalSave;
        const documentsDiffer = !isEqual(localData.document, apiDocument);
        // Reconcile the local draft with the server copy:
        //  - 'recover'  → genuine unsynced edits, server not ahead: load local.
        //  - 'server'   → local is stale/clean: server is authoritative.
        //  - 'conflict' → both advanced independently: ask the user (block the
        //    editor behind DraftConflictModal). Never silently drop either side
        //    (data-loss guard, Smartcomtv incident 2026-06).
        const decision = resolveDraftRecovery({
          localIsDirty: localData.isDirty,
          localLastSave: localTimestamp,
          localLastServerSync: localData.lastServerSync,
          serverLastModified: serverTimestamp,
          documentsDiffer
        });

        if (decision === 'recover') {
          recoveredData = {
            document: localData.document,
            undoStack: localData.undoStack || [],
            redoStack: localData.redoStack || [],
            // isDirty is a per-session flag — always start false on open.
            // saveStatus='saved-locally' already communicates the pending state.
            isDirty: false,
            recoveryTimestamp: localTimestamp || Date.now()
          };
        } else if (decision === 'conflict') {
          // Keep the server copy as the safe live baseline; capture the local
          // draft so the modal can offer it. Nothing is applied until the user
          // chooses, so no autosave can clobber either version.
          initialConflictRef.current = {
            localDocument: localData.document,
            localUndo: localData.undoStack || [],
            localRedo: localData.redoStack || [],
            localTimestamp: localTimestamp || 0,
            serverTimestamp,
            localCount: localData.document.document?.length,
            serverCount: apiDocument?.document?.length
          };
        }
      }
    }

    const finalDocument = recoveredData?.document ?? apiDocument;
    hasRecoveredData.current = !!recoveredData;

    let saveStatus: 'unsaved' | 'saved-locally' | 'synced' = 'synced';
    if (recoveredData) {
      saveStatus = recoveredData.recoveryTimestamp
        ? 'saved-locally'
        : 'unsaved';
    }

    initialStateRef.current = {
      ...initialState,
      session,
      document: finalDocument,
      documentResult: fetchedDocument,
      undoStack: recoveredData?.undoStack ?? [],
      redoStack: recoveredData?.redoStack ?? [],
      isDirty: recoveredData?.isDirty ?? false,
      saveStatus,
      lastLocalSave: recoveredData?.recoveryTimestamp ?? null
    };
  }

  const [state, dispatch] = useReducer(reducer, initialStateRef.current);

  // Unresolved local-vs-cloud conflict. While set, the editor is blocked behind
  // DraftConflictModal so neither version can be silently overwritten.
  const [conflict, setConflict] =
    useState<DraftConflictData<DocumentSubtitleV1> | null>(
      () => initialConflictRef.current
    );

  // Keep session in sync
  useEffect(() => {
    if (session && session !== state.session) {
      dispatch({ type: 'SET_SESSION', payload: session });
    }
  }, [session]);

  // Sync document from API (after initial mount)
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (!fetchedDocument) return;
    // Always sync the result metadata (id, filename, last_modified) once the
    // fetch resolves — saveDocument reads `state.documentResult.result.id`,
    // so without this the translation pane can't save when the query resolves
    // after mount AND there's a recovered local draft (the old
    // `hasRecoveredData` short-circuit blocked both dispatches).
    dispatch({ type: 'SET_DOCUMENT_RESULT', payload: fetchedDocument });
    // The document body is a different story: a recovered draft holds the
    // user's unsaved edits, so skip overwriting it from the server copy.
    if (hasRecoveredData.current || documentRef.current) return;
    dispatch({
      type: 'SET_DOCUMENT',
      payload: fetchedDocument.result.document as DocumentSubtitleV1
    });
    documentRef.current = fetchedDocument.result.document as DocumentSubtitleV1;
    // The body was adopted, so this pane is now based on that server version
    // and the baseline may advance with it. This is deliberately inside the
    // adopt branch: see the seeding effect below.
    if (sessionId) {
      setBaseline(
        syncKey(sessionId, fetchedDocument.result.id),
        fetchedDocument.result.last_modified
      );
    }
  }, [fetchedDocument]);

  // Seed the autosave baseline with the server version this pane opened on.
  // The translation pane has no concurrent-edit poller to do it, and without a
  // baseline every autosave would resolve to `skip-unverified` and never run.
  //
  // Once only, and never on a later refetch (#2354 review): the metadata sync
  // above deliberately keeps a recovered local body while refreshing
  // `documentResult`, so a refetch that advanced the baseline would claim this
  // pane is based on a server revision whose body it never adopted — and the
  // next autosave would then overwrite that revision's edits. The baseline
  // advances only where the body does: on adoption above, on our own save, or
  // on an explicit reload.
  const hasSeededBaseline = useRef(false);
  useEffect(() => {
    if (hasSeededBaseline.current) return;
    if (!sessionId || !fetchedDocument) return;
    hasSeededBaseline.current = true;
    setBaseline(
      syncKey(sessionId, fetchedDocument.result.id),
      fetchedDocument.result.last_modified
    );
  }, [sessionId, fetchedDocument]);

  // Mutation
  const { mutate: updateDocument, isLoading: saveDocumentLoading } =
    useUpdateDocument();

  const currentEditorRef = useRef<ReactEditor | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const documentRef = useRef<DocumentSubtitleV1 | undefined>(
    initialStateRef.current.document
  );
  const undoStackRef = useRef<DocumentSubtitleV1[]>(
    initialStateRef.current.undoStack
  );
  const redoStackRef = useRef<DocumentSubtitleV1[]>(
    initialStateRef.current.redoStack
  );

  // Keep refs in sync
  useEffect(() => {
    undoStackRef.current = state.undoStack;
    redoStackRef.current = state.redoStack;
  }, [state.undoStack, state.redoStack]);

  // ---- Helpers ----

  const setSaveStatus = useCallback(
    (status: 'unsaved' | 'saved-locally' | 'synced' | 'saving') => {
      dispatch({ type: 'SET_SAVE_STATUS', payload: status });
    },
    []
  );

  const setLastLocalSave = useCallback((timestamp: number | null) => {
    dispatch({ type: 'SET_LAST_LOCAL_SAVE', payload: timestamp });
  }, []);

  const setLastServerSync = useCallback((timestamp: number | null) => {
    dispatch({ type: 'SET_LAST_SERVER_SYNC', payload: timestamp });
  }, []);

  const setIsDirty = useCallback((isDirty: boolean) => {
    dispatch({ type: 'SET_IS_DIRTY', payload: isDirty });
  }, []);

  // localStorage debounce
  const localStorageSaveTimer = useRef<NodeJS.Timeout | null>(null);

  const debouncedLocalStorageSave = useCallback(() => {
    if (!sessionId || !state.documentResult) return;
    if (localStorageSaveTimer.current) {
      clearTimeout(localStorageSaveTimer.current);
    }
    localStorageSaveTimer.current = setTimeout(() => {
      const currentDocument = documentRef.current;
      if (!currentDocument || !state.documentResult) return;
      const docId = state.documentResult.result.id;
      const success = saveCaptionToLocalStorage(sessionId, docId, {
        document: currentDocument,
        undoStack: undoStackRef.current,
        redoStack: redoStackRef.current,
        isDirty: true,
        filename: state.documentResult.result.filename,
        timecodeOffset: state.documentResult.result.timecode_offset
      });
      if (success) {
        setSaveStatus('saved-locally');
        setLastLocalSave(Date.now());
      }
    }, LOCALSTORAGE_DEBOUNCE_MS);
  }, [sessionId, state.documentResult, setSaveStatus, setLastLocalSave]);

  useEffect(() => {
    return () => {
      if (localStorageSaveTimer.current) {
        clearTimeout(localStorageSaveTimer.current);
      }
    };
  }, []);

  // ---- Version / undo / redo ----

  // Current typing run; see startsTextUndoStep.
  const textUndoStepRef = useRef<TextUndoStep>(null);
  const [historyVersion, setHistoryVersion] = useState(0);

  const addVersionChange = useCallback(
    (newDocument?: DocumentSubtitleV1) => {
      textUndoStepRef.current = null;
      // Cheap structural equality instead of two full-document JSON.stringify
      // calls (multi-MB transient garbage per edit on large documents).
      if (!state.document || isEqual(state.document, newDocument || {})) {
        return;
      }
      const nextUndo = [...state.undoStack, state.document];
      const maxHistory = getMaxHistory(state.document);
      const trimmedUndo =
        nextUndo.length > maxHistory
          ? nextUndo.slice(nextUndo.length - maxHistory)
          : nextUndo;

      dispatch({ type: 'SET_UNDO_STACK', payload: trimmedUndo });
      dispatch({ type: 'SET_REDO_STACK', payload: [] });

      if (sessionId && state.documentResult) {
        saveCaptionToLocalStorage(sessionId, state.documentResult.result.id, {
          isDirty: true,
          document: newDocument ?? documentRef.current
        });
      }
    },
    [state.document, state.undoStack, sessionId, state.documentResult]
  );

  const clearHistoryStack = useCallback(() => {
    dispatch({ type: 'SET_UNDO_STACK', payload: [] });
    dispatch({ type: 'SET_REDO_STACK', payload: [] });
  }, []);

  // ---- Core state setters ----

  const setDocument = useCallback(
    (document: DocumentSubtitleV1) => {
      if (document === state.document) return;
      addVersionChange(document);
      dispatch({ type: 'SET_DOCUMENT', payload: document });
      documentRef.current = document;
      setIsDirty(true);
      setSaveStatus('unsaved');
      debouncedLocalStorageSave();
    },
    [
      state.document,
      addVersionChange,
      setIsDirty,
      setSaveStatus,
      debouncedLocalStorageSave
    ]
  );

  const setQuery = useCallback((query: string) => {
    dispatch({ type: 'SET_QUERY', payload: query });
  }, []);

  const setEditor = useCallback((editor: ReactEditor | null) => {
    currentEditorRef.current = editor;
  }, []);

  const setClickedIndex = useCallback((id: number | null) => {
    dispatch({ type: 'SET_CLICKED_INDEX', payload: id });
  }, []);

  // ---- Undo / Redo ----

  const undo = useCallback(() => {
    textUndoStepRef.current = null;
    if (state.undoStack.length === 0) return;
    const undoStackCopy = [...state.undoStack];
    const lastState = undoStackCopy.pop();
    if (lastState) {
      const newRedoStack = state.document
        ? [...state.redoStack, state.document]
        : state.redoStack;
      if (state.document) {
        dispatch({ type: 'SET_REDO_STACK', payload: newRedoStack });
      }
      dispatch({ type: 'SET_UNDO_STACK', payload: undoStackCopy });
      dispatch({ type: 'SET_DOCUMENT', payload: lastState });
      documentRef.current = lastState;
      setHistoryVersion(v => v + 1);
      if (sessionId && state.documentResult) {
        saveCaptionToLocalStorage(sessionId, state.documentResult.result.id, {
          document: lastState,
          undoStack: undoStackCopy,
          redoStack: newRedoStack,
          isDirty: true
        });
        // Keep the React flag in step with the persisted draft (issue #2347):
        // a dirty draft the autosave gate reads as clean never reaches the
        // server.
        setIsDirty(true);
        setSaveStatus('saved-locally');
        setLastLocalSave(Date.now());
      }
    }
  }, [
    state.undoStack,
    state.redoStack,
    state.document,
    state.documentResult,
    sessionId,
    setSaveStatus,
    setLastLocalSave
  ]);

  const redo = useCallback(() => {
    textUndoStepRef.current = null;
    if (state.redoStack.length === 0) return;
    const redoStackCopy = [...state.redoStack];
    const nextState = redoStackCopy.pop();
    if (nextState) {
      const newUndoStack = state.document
        ? [...state.undoStack, state.document]
        : state.undoStack;
      if (state.document) {
        dispatch({ type: 'SET_UNDO_STACK', payload: newUndoStack });
      }
      dispatch({ type: 'SET_REDO_STACK', payload: redoStackCopy });
      dispatch({ type: 'SET_DOCUMENT', payload: nextState });
      documentRef.current = nextState;
      setHistoryVersion(v => v + 1);
      if (sessionId && state.documentResult) {
        saveCaptionToLocalStorage(sessionId, state.documentResult.result.id, {
          document: nextState,
          undoStack: newUndoStack,
          redoStack: redoStackCopy,
          isDirty: true
        });
        setIsDirty(true);
        setSaveStatus('saved-locally');
        setLastLocalSave(Date.now());
      }
    }
  }, [
    state.redoStack,
    state.undoStack,
    state.document,
    state.documentResult,
    sessionId,
    setSaveStatus,
    setLastLocalSave
  ]);

  // ---- Segment operations ----

  const addListItem = useCallback(
    (id: number) => {
      if (!state.document?.document) return;
      const currentItemIndex = state.document.document.findIndex(
        d => d.id === id
      );
      const currentItem = state.document.document[currentItemIndex];
      const nextItem = state.document.document[currentItemIndex + 1];
      const MIN_DURATION = 100;
      const DEFAULT_DURATION = 1000;
      const newStartTime = currentItem.stop + 1;
      let newStopTime;
      if (nextItem) {
        const availableSpace = nextItem.start - newStartTime;
        if (availableSpace < MIN_DURATION) {
          toast(t('insufficient-space-between-segments'), {
            id: 'insufficient-space-between-segments',
            icon: '⚠️'
          });
          return;
        }
        const duration = Math.min(availableSpace - 1, DEFAULT_DURATION);
        newStopTime = newStartTime + duration;
      } else {
        const currentDuration = currentItem.stop - currentItem.start;
        newStopTime =
          newStartTime + Math.max(currentDuration, DEFAULT_DURATION);
      }
      const modifiedDocument = state.document.document.map(item =>
        item.id > id ? { ...item, id: item.id + 1 } : item
      );
      const newItem: DocumentSubtitleSegmentV1 = {
        id: currentItem.id + 1,
        start: newStartTime,
        stop: newStopTime,
        text: '',
        speaker: ''
      };
      modifiedDocument.splice(currentItemIndex + 1, 0, newItem);
      setDocument({ ...state.document, document: modifiedDocument });
      setIsDirty(true);
    },
    [state.document, setDocument, setIsDirty, t]
  );

  const removeListItem = useCallback(
    (id: number) => {
      if (!state.document?.document) return;
      const updatedDocument = state.document.document.filter(
        item => item.id !== id
      );
      setDocument({ ...state.document, document: updatedDocument });
      setIsDirty(true);
    },
    [state.document, setDocument, setIsDirty]
  );

  const splitListItem = useCallback(
    (id: number) => {
      if (!state.document?.document) return;
      const currentItemIndex = state.document.document.findIndex(
        d => d.id === id
      );
      const lastItem = state.document.document[currentItemIndex];
      if (!lastItem.text || lastItem.text.trim().length <= 2) {
        toast.error(t('segment-is-too-short-to-split'));
        return;
      }
      const middleTime = Math.floor((lastItem.stop + lastItem.start) / 2);
      const middleIndex = Math.floor(lastItem.text.length / 2);
      const nearestSpace =
        lastItem.text.lastIndexOf(' ', middleIndex) + 1 || middleIndex;
      const modifiedDocument = state.document.document.map(item =>
        item.id > id ? { ...item, id: item.id + 1 } : item
      );
      const newItem: DocumentSubtitleSegmentV1 = {
        id: lastItem.id + 1,
        start: middleTime,
        stop: lastItem.stop,
        text: lastItem.text.substring(nearestSpace).trim(),
        speaker: lastItem.speaker
      };
      modifiedDocument[currentItemIndex] = {
        ...lastItem,
        stop: middleTime,
        text: lastItem.text.substring(0, nearestSpace).trim()
      };
      modifiedDocument.splice(currentItemIndex + 1, 0, newItem);
      setDocument({ ...state.document, document: modifiedDocument });
      setIsDirty(true);
    },
    [state.document, setDocument, setIsDirty, t]
  );

  const joinListItems = useCallback(
    (id: number) => {
      if (!state.document?.document) return;
      const currentItemIndex = state.document.document.findIndex(
        d => d.id === id
      );
      if (currentItemIndex === -1) {
        toast.error(t('segment-not-found'));
        return;
      }
      const currentItem = state.document.document[currentItemIndex];
      const nextItem = state.document.document[currentItemIndex + 1];
      if (!nextItem) {
        toast.error(t('no-next-segment-to-join'));
        return;
      }
      const mergedText = currentItem.text + '\n' + nextItem.text;
      const mergedSegment: DocumentSubtitleSegmentV1 = {
        ...currentItem,
        stop: nextItem.stop,
        text: mergedText
      };
      const modifiedDocument = state.document.document
        .filter((_, index) => index !== currentItemIndex + 1)
        .map((item, index) => {
          if (index === currentItemIndex) return mergedSegment;
          if (item.id > nextItem.id) return { ...item, id: item.id - 1 };
          return item;
        });
      setDocument({ ...state.document, document: modifiedDocument });
      setIsDirty(true);
      toast.success(t('segments-joined-successfully'));
    },
    [state.document, setDocument, setIsDirty, t]
  );

  // ---- Style ----

  const applyStyle = useCallback(
    (
      style: 'bold' | 'italic' | 'underlined' | 'color' | 'strike' | 'position',
      param2?: string
    ) => {
      if (!state.document) return;
      let idx = state.clickedIndex;
      if (idx === null || idx === undefined || idx < 0) {
        const currentTimeMs = currentTime * 1000;
        idx = state.document.document.findIndex(
          seg => currentTimeMs >= seg.start && currentTimeMs <= seg.stop
        );
      }
      if (idx === null || idx === undefined || idx < 0) {
        if (state.document.document.length > 0) idx = 0;
        else return;
      }
      const updatedDocument = {
        ...state.document,
        document: state.document.document.map((segment, index) => {
          if (index !== idx) return segment;
          const updated = { ...segment };
          switch (style) {
            case 'color':
              if (param2) updated.color = param2;
              break;
            case 'bold':
            case 'italic':
            case 'underlined':
            case 'strike':
              updated[style] = !updated[style];
              break;
            case 'position':
              if (param2 !== undefined) {
                const posNum = parseInt(param2, 10);
                if (!isNaN(posNum)) {
                  const clamped = Math.max(0, Math.min(100, posNum));
                  updated.position =
                    clamped === 100 ? undefined : String(clamped);
                }
              }
              break;
          }
          return updated;
        })
      };
      setDocument(updatedDocument);
      setIsDirty(true);
    },
    [state.document, state.clickedIndex, currentTime, setDocument, setIsDirty]
  );

  const applyFontToAll = useCallback(
    (fontFamily: string) => {
      if (!state.document) return;
      const updatedDocument = {
        ...state.document,
        document: state.document.document.map(segment => ({
          ...segment,
          fontFamily: fontFamily || undefined
        }))
      };
      setDocument(updatedDocument);
      setIsDirty(true);
    },
    [state.document, setDocument, setIsDirty]
  );

  // ---- Timing ----

  const changeSegmentTime = useCallback(
    (
      id: number,
      field: 'start' | 'stop',
      name: string = 'ms',
      value: string = '0'
    ) => {
      if (!state.document) return;
      let numericValue = parseInt(value, 10);
      if (isNaN(numericValue)) numericValue = 0;

      const currentItemIndex = state.document.document.findIndex(
        d => d.id === id
      );
      const segment = state.document.document[currentItemIndex];
      let newTime: number;

      if (name === 'ms') {
        newTime = numericValue;
      } else {
        const baseTime = field === 'start' ? segment.start : segment.stop;
        const date = new Date(baseTime);
        switch (name) {
          case 'hours':
            date.setHours(numericValue);
            break;
          case 'minutes':
            date.setMinutes(numericValue);
            break;
          case 'seconds':
            date.setSeconds(numericValue);
            break;
          default:
            return;
        }
        newTime = date.getTime();
      }

      const updatedDocument: DocumentSubtitleV1 = {
        ...state.document,
        document: state.document.document.map(
          (docItem: DocumentSubtitleSegmentV1) =>
            docItem.id === id ? { ...docItem, [field]: newTime } : docItem
        )
      };
      setDocument(updatedDocument);
    },
    [state.document, setDocument]
  );

  const changeSegmentTimeForCurrentIndex = useCallback(
    (field: 'start' | 'stop', milliseconds: number) => {
      if (
        state.clickedIndex === undefined ||
        state.clickedIndex === null ||
        !state.document
      )
        return;
      let time =
        field === 'start'
          ? state.document.document[state.clickedIndex].start
          : state.document.document[state.clickedIndex].stop;
      time += milliseconds;
      const updatedDocument: DocumentSubtitleV1 = {
        ...state.document,
        document: state.document.document.map(
          (docItem: DocumentSubtitleSegmentV1, idx: number) =>
            idx === state.clickedIndex ? { ...docItem, [field]: time } : docItem
        )
      };
      setDocument(updatedDocument);
    },
    [state.document, state.clickedIndex, setDocument]
  );

  const setTimeAndSegment = useCallback(
    (start: number) => {
      setTime(start);
      const time = start * 1000;
      const segmentIndex = state.document?.document.findIndex(
        segment => time >= segment.start && time <= segment.stop
      );
      if (segmentIndex === undefined || segmentIndex === -1) {
        setClickedIndex(null);
        return;
      }
      setClickedIndex(segmentIndex);
    },
    [state.document, setTime, setClickedIndex]
  );

  // ---- Active index ----

  const getActiveIndex = useCallback((): number | null => {
    const list = state.document?.document ?? [];
    if (!list.length) return null;
    const n = state.clickedIndex;
    if (n === null || n === undefined) return null;
    if (typeof n === 'number' && n >= 0 && n < list.length) return n;
    const idxFromId = list.findIndex(s => s.id === n);
    return idxFromId === -1 ? null : idxFromId;
  }, [state.document?.document, state.clickedIndex]);

  // ---- Timing offset ----

  const applyTimingOffset = useCallback(
    (deltaMs: number, mode: 'auto' | 'all' | 'fromActive' = 'auto') => {
      const doc = state.document;
      if (!doc?.document?.length || !deltaMs) return;
      const items = doc.document;
      const activeIdx = getActiveIndex();
      const fromIndex =
        mode === 'all'
          ? 0
          : mode === 'fromActive'
            ? (activeIdx ?? 0)
            : (activeIdx ?? 0);

      const shifted = items.map((seg, i) => {
        if (i < fromIndex) return seg;
        const newStart = Math.max(0, seg.start + deltaMs);
        const actualShift = newStart - seg.start;
        const newStop = Math.max(newStart, seg.stop + actualShift);
        return { ...seg, start: newStart, stop: newStop };
      });

      const newPayload = { ...doc, document: shifted };
      addVersionChange(newPayload);
      dispatch({ type: 'SET_DOCUMENT', payload: newPayload });
      documentRef.current = newPayload;
      setIsDirty(true);
      setSaveStatus('unsaved');
      debouncedLocalStorageSave();
    },
    [
      state.document,
      getActiveIndex,
      addVersionChange,
      setIsDirty,
      setSaveStatus,
      debouncedLocalStorageSave
    ]
  );

  // ---- Editor update (called on every Slate keystroke) ----

  const editorUpdateSegment = useCallback(
    (children: Descendant[]) => {
      if (!state.document || !children || children.length === 0) return;

      const childrenDocument = children.find(
        child => typeof (child as CaptionScriptixDescendant).id === 'number'
      ) as CaptionScriptixDescendant & {
        children: (Descendant & { text: string })[];
      };
      if (!childrenDocument || typeof childrenDocument.id !== 'number') return;

      const segment = state.document.document.find(
        s => s.id === childrenDocument.id
      );
      if (!segment) return;

      const index = state.document.document.indexOf(segment);
      if (index === -1) return;

      const newText = childrenDocument.children?.[0]?.text ?? '';
      if (segment.text === newText) return;

      const updatedDocument = [...state.document.document];
      updatedDocument[index] = { ...segment, text: newText };

      const newPayload: DocumentSubtitleV1 = {
        ...state.document,
        document: updatedDocument
      };

      const now = Date.now();
      if (startsTextUndoStep(textUndoStepRef.current, segment.id, now)) {
        addVersionChange(newPayload);
      }
      textUndoStepRef.current = { segmentId: segment.id, at: now };

      dispatch({ type: 'SET_DOCUMENT', payload: newPayload });
      documentRef.current = newPayload;
      setIsDirty(true);
      setSaveStatus('unsaved');
      debouncedLocalStorageSave();
    },
    [
      state.document,
      addVersionChange,
      setIsDirty,
      setSaveStatus,
      debouncedLocalStorageSave
    ]
  );

  // ---- Save ----

  /**
   * Record the server version produced by this pane's own save so the next
   * autosave does not mistake our own write for somebody else's. Prefers the
   * `last_modified` echoed by the PUT response, falling back to one lightweight
   * probe; if both fail the baseline stays put and the next autosave holds the
   * draft locally rather than overwriting on no evidence (issue #2347).
   */
  const recordOwnSave = useCallback(
    async (documentId: string, response: unknown) => {
      if (!sessionId) return;
      const key = syncKey(sessionId, documentId);
      const fromResponse = lastModifiedFromUpdateResponse(response);
      if (fromResponse > 0) {
        setBaseline(key, fromResponse);
        return;
      }
      const probed = await probeServerLastModified(sessionId, documentId);
      if (probed != null) setBaseline(key, probed);
    },
    [sessionId]
  );

  const saveDocument = useCallback(
    (userAction?: boolean, onSaved?: () => void) => {
      if (!state.document || !state.documentResult || !sessionId) return;

      // Data-loss guard (Den Haag incident, 2026-06): never PUT an empty
      // document. An empty editor state is corruption, not an edit, and saving
      // it wipes the server copy's cues.
      if (!state.document.document || state.document.document.length === 0) {
        setSaveStatus(state.lastLocalSave ? 'saved-locally' : 'unsaved');
        return;
      }

      const documentAtSave = state.document;
      setSaveStatus('saving');
      updateDocument(
        {
          sessionId,
          documentId: state.documentResult.result.id!,
          document: state.document as DocumentObjectInnerDocument,
          plain_document_changed: !!userAction
        },
        {
          onSuccess: response => {
            toast.success(t('document-updated-successfully'));
            const now = Date.now();
            void recordOwnSave(state.documentResult!.result.id!, response);
            setLastServerSync(now);
            onSaved?.();
            if (documentRef.current !== documentAtSave) {
              setSaveStatus('unsaved');
              return;
            }
            setIsDirty(false);
            setSaveStatus('synced');
            hasRecoveredData.current = false;
            if (state.documentResult && state.document) {
              saveCaptionToLocalStorage(
                sessionId,
                state.documentResult.result.id,
                {
                  document: state.document,
                  undoStack: state.undoStack,
                  redoStack: state.redoStack,
                  isDirty: false,
                  lastLocalSave: state.lastLocalSave ?? undefined,
                  lastServerSync: now,
                  filename: state.documentResult.result.filename,
                  timecodeOffset: state.documentResult.result.timecode_offset
                }
              );
            }
          },
          onError: () => {
            toast.error(t('error-updating-document'));
            setSaveStatus(state.lastLocalSave ? 'saved-locally' : 'unsaved');
          }
        }
      );
    },
    [
      state.document,
      state.documentResult,
      state.undoStack,
      state.redoStack,
      state.lastLocalSave,
      sessionId,
      updateDocument,
      recordOwnSave,
      setSaveStatus,
      setIsDirty,
      setLastServerSync,
      t
    ]
  );

  /**
   * Autosave for the translation pane, fired by the editor's 120-second
   * interval alongside the source pane's.
   *
   * Stale-tab guard (issue #2347): a pane left open on an old copy used to PUT
   * that entire copy every interval, replacing newer edits saved from another
   * tab — including another tab of the same account. It now refuses to write
   * when there is nothing to save, when the server's state cannot be read, or
   * when the server has moved past the version this pane is based on (which
   * also raises the source editor's concurrent-edit banner).
   */
  const silentSave = useCallback(async () => {
    if (!state.documentResult || !sessionId) return;

    const documentId = state.documentResult.result.id!;
    const key = syncKey(sessionId, documentId);
    const docToSave = state.document ?? documentRef.current;

    // Data-loss guard (Den Haag incident, 2026-06): never autosave an empty
    // document — `resolveAutosave` reports `skip-empty`.
    const gateInput = {
      segmentCount: docToSave?.document?.length ?? 0,
      hasLocalChanges: hasPendingLocalChanges(state.isDirty, state.saveStatus),
      baselineLastModified: getBaseline(key)
    };

    let decision = resolveAutosave(gateInput);
    if (decision === 'needs-probe') {
      const serverLastModified = await probeServerLastModified(
        sessionId,
        documentId
      );
      decision = resolveAutosave({
        ...gateInput,
        baselineLastModified: getBaseline(key),
        serverLastModified
      });
    }

    if (decision === 'conflict') {
      notifyRemoteEdit(key);
      setHasRemoteEdit(true);
      return;
    }

    if (decision !== 'save') return;

    // Re-read the live document now that the preflight has resolved. The probe
    // is a network round trip, and anything the user typed during it landed in
    // `documentRef` after `docToSave` was captured — sending that stale snapshot
    // and then clearing the dirty flag would strand those keystrokes, because
    // the next tick would read the pane as clean and skip them (#2354 review).
    const docAtSend = documentRef.current ?? state.document;
    if (!docAtSend?.document || docAtSend.document.length === 0) return;

    setSaveStatus('saving');
    updateDocument(
      {
        sessionId,
        documentId,
        document: docAtSend as DocumentObjectInnerDocument,
        dontInvalidateQueries: true
      },
      {
        onSuccess: response => {
          // The server moved either way, so the baseline always advances.
          void recordOwnSave(documentId, response);
          const now = Date.now();
          setLastServerSync(now);

          // Only the revision we actually sent is clean. Every mutation path
          // replaces the document object and reassigns `documentRef`, so an
          // identity check tells us whether the user edited while the PUT was
          // in flight. If they did, leave the dirty flag alone so the next
          // autosave pushes the newer text.
          if (documentRef.current !== docAtSend) {
            setSaveStatus('unsaved');
            return;
          }

          setIsDirty(false);
          setSaveStatus('synced');
          hasRecoveredData.current = false;
          if (state.documentResult) {
            markAsSynced(sessionId, state.documentResult.result.id);
          }
        },
        onError: () => {
          setSaveStatus(state.lastLocalSave ? 'saved-locally' : 'unsaved');
        }
      }
    );
  }, [
    state.document,
    state.documentResult,
    state.lastLocalSave,
    // The autosave gate reads both change signals; without them in the deps the
    // memoised callback would decide from a stale snapshot.
    state.isDirty,
    state.saveStatus,
    sessionId,
    updateDocument,
    recordOwnSave,
    setSaveStatus,
    setIsDirty,
    setLastServerSync
  ]);

  // ---- Search ----

  const setSearch = useCallback((query: string) => {
    dispatch({ type: 'SET_SEARCH', payload: query });
  }, []);

  const setCurrentMatch = useCallback(
    (match: DocumentSubtitleSegmentV1[] | null) => {
      dispatch({ type: 'SET_CURRENT_MATCH', payload: match });
    },
    []
  );

  const setCaseSensitive = useCallback((caseSensitive: boolean) => {
    dispatch({ type: 'SET_CASE_SENSITIVE', payload: caseSensitive });
  }, []);

  const setWholeWord = useCallback((wholeWord: boolean) => {
    dispatch({ type: 'SET_WHOLE_WORD', payload: wholeWord });
  }, []);

  /**
   * Replace the whole document from a bulk operation (Find and Replace, or a
   * font applied to every cue).
   *
   * Dirty tracking (issue #2347): this used to update state and the ref only,
   * so a bulk edit was never flagged dirty, never written to localStorage, and
   * indistinguishable from an untouched tab.
   */
  const updateCaptionDocument = useCallback(
    (document: DocumentSubtitleV1) => {
      dispatch({ type: 'SET_DOCUMENT', payload: document });
      documentRef.current = document;
      setIsDirty(true);
      setSaveStatus('unsaved');
      debouncedLocalStorageSave();
    },
    [setIsDirty, setSaveStatus, debouncedLocalStorageSave]
  );

  const updateDocumentResult = useCallback((documentResult: DocumentResult) => {
    dispatch({ type: 'SET_DOCUMENT_RESULT', payload: documentResult });
  }, []);

  const reloadFromServer = useCallback(
    (freshDocument: DocumentResult) => {
      const newDocument = freshDocument.result.document as DocumentSubtitleV1;
      if (sessionId && state.documentResult) {
        clearCaptionFromLocalStorage(sessionId, state.documentResult.result.id);
      }
      dispatch({ type: 'SET_DOCUMENT', payload: newDocument });
      dispatch({ type: 'SET_DOCUMENT_RESULT', payload: freshDocument });
      documentRef.current = newDocument;
      if (sessionId) {
        setBaseline(
          syncKey(sessionId, freshDocument.result.id),
          freshDocument.result.last_modified
        );
      }
      clearHistoryStack();
      undoStackRef.current = [];
      redoStackRef.current = [];
      dispatch({ type: 'INCREMENT_DOCUMENT_VERSION', payload: null });
      hasRecoveredData.current = false;
      setIsDirty(false);
      setSaveStatus('synced');
      setLastServerSync(Date.now());
    },
    [
      sessionId,
      state.documentResult,
      clearHistoryStack,
      setIsDirty,
      setSaveStatus,
      setLastServerSync
    ]
  );

  const reloadTranslation = useCallback(async () => {
    if (!documentId || !sessionId) return;
    try {
      const documentBeforeReload = documentRef.current;
      const freshDocument = await getDocument({ sessionId, documentId });
      const remoteBody = freshDocument.result.document as
        | DocumentSubtitleV1
        | undefined;
      if (!remoteBody?.document?.length) {
        throw new Error('Reload returned an empty caption document');
      }
      if (documentRef.current !== documentBeforeReload) {
        toast.error(
          t(
            'document-changed-while-reloading',
            'Document changed while reloading. Please try again.'
          )
        );
        return;
      }
      queryClient.setQueryData(
        QueryKeys.document(sessionId, documentId),
        freshDocument
      );
      reloadFromServer(freshDocument);
      setHasRemoteEdit(false);
    } catch {
      toast.error(t('error-fetching-data'));
    }
  }, [documentId, sessionId, queryClient, reloadFromServer, t]);

  // ---- No-ops for source-only operations ----

  const reset = useCallback(() => {
    // No-op for translation pane
  }, []);

  const cleanDocument = useCallback(() => {
    // No-op for translation pane
  }, []);

  const handleMediaElementReady = useCallback(() => {
    // No-op for translation pane (Peaks is source-only)
  }, []);

  const finishDocument = useCallback(() => {
    // No-op for translation pane
  }, []);

  // ---- Expose handle to parent ----

  useImperativeHandle(
    handleRef,
    () => ({
      saveDocument,
      isDirty: state.isDirty ?? false,
      saveStatus: state.saveStatus,
      autoSave: silentSave,
      undo,
      redo
    }),
    [saveDocument, state.isDirty, state.saveStatus, silentSave, undo, redo]
  );

  useEffect(() => {
    onDirtyChange?.(state.isDirty ?? false);
  }, [state.isDirty, onDirtyChange]);

  // --- Conflict resolution (DraftConflictModal) -----------------------------
  // Apply the local draft over the server baseline and continue editing it.
  const resolveConflictKeepLocal = useCallback(() => {
    if (!conflict) return;
    dispatch({ type: 'SET_DOCUMENT', payload: conflict.localDocument });
    dispatch({ type: 'SET_UNDO_STACK', payload: conflict.localUndo });
    dispatch({ type: 'SET_REDO_STACK', payload: conflict.localRedo });
    documentRef.current = conflict.localDocument;
    undoStackRef.current = conflict.localUndo;
    redoStackRef.current = conflict.localRedo;
    // Prevent the API document from overwriting the recovered local draft.
    hasRecoveredData.current = true;
    // The user has seen the cloud version in the conflict modal and chose this
    // draft over it, so the tab is now deliberately based on that server
    // version: autosave is free to overwrite it (issue #2347). Without this the
    // gate would keep reporting a conflict and only an explicit save would get
    // through.
    if (sessionId && state.documentResult) {
      setBaseline(
        syncKey(sessionId, state.documentResult.result.id),
        conflict.serverTimestamp
      );
    }
    setIsDirty(true);
    setSaveStatus('saved-locally');
    setLastLocalSave(conflict.localTimestamp || Date.now());
    setConflict(null);
  }, [
    conflict,
    sessionId,
    state.documentResult,
    setIsDirty,
    setSaveStatus,
    setLastLocalSave
  ]);

  // Discard the local draft and keep the server copy (already the live state).
  const resolveConflictLoadCloud = useCallback(() => {
    if (sessionId && state.documentResult) {
      clearCaptionFromLocalStorage(sessionId, state.documentResult.result.id);
    }
    setSaveStatus('synced');
    setConflict(null);
  }, [sessionId, state.documentResult, setSaveStatus]);

  // ---- Build context value ----

  const contextValue: CaptionContextType = {
    ...state,
    documentResponse: undefined,
    setQuery,
    setDocument,
    historyVersion,
    undo,
    redo,
    reset,
    addListItem,
    removeListItem,
    splitListItem,
    joinListItems,
    cleanDocument,
    saveDocument,
    searchInputRef,
    setClickedIndex,
    cleanup: clearHistoryStack,
    applyStyle,
    applyFontToAll,
    saveDocumentLoading,
    documentResetting: false,
    cleanDocumentLoading: false,
    finishedLoading: false,
    changeSegmentTime,
    changeSegmentTimeForCurrentIndex,
    setTimeAndSegment,
    handleMediaElementReady,
    finishDocument,
    setEditor,
    editorUpdateSegment,
    autoSave: silentSave,
    setSearch,
    setCurrentMatch,
    setCaseSensitive,
    setWholeWord,
    updateCaptionDocument,
    applyTimingOffset,
    getActiveIndex,
    saveStatus: state.saveStatus,
    lastLocalSave: state.lastLocalSave,
    lastServerSync: state.lastServerSync,
    updateDocumentResult,
    reloadFromServer
  };

  return (
    <CaptionContext.Provider value={contextValue}>
      {conflict ? (
        <DraftConflictModal
          isOpen
          localSavedAt={conflict.localTimestamp}
          serverSavedAt={conflict.serverTimestamp}
          localCount={conflict.localCount}
          serverCount={conflict.serverCount}
          unit={t('caption-cues-unit', 'cues')}
          onKeepLocal={resolveConflictKeepLocal}
          onLoadCloud={resolveConflictLoadCloud}
          onViewCloud={openCloudVersionInNewTab}
        />
      ) : (
        <>
          {children}
          {hasRemoteEdit && (
            <ConcurrentEditBanner
              documentLabel={t('translation')}
              positionClassName="top-52"
              isSaving={saveDocumentLoading}
              onReload={reloadTranslation}
              onDismiss={() => setHasRemoteEdit(false)}
              onSaveMyChanges={() =>
                saveDocument(true, () => setHasRemoteEdit(false))
              }
              hasUnsavedChanges={hasPendingLocalChanges(
                state.isDirty,
                state.saveStatus
              )}
            />
          )}
        </>
      )}
    </CaptionContext.Provider>
  );
}
